import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui";

const DeleteModal = ({
  text,
  showModal,
  confirmDelete,
  onClose,
  isLoading = false,
  confirmText = "confirm",
  confirmIcon = null,
}) => {
  const { t, i18n } = useTranslation("translation");
  const cancelButtonRef = useRef(null);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !isLoading) {
        onClose();
      }
    };

    if (showModal) {
      document.addEventListener("keydown", handleKeyDown);
      if (cancelButtonRef.current) {
        cancelButtonRef.current.focus();
      }
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [showModal, isLoading, onClose]);

  if (!showModal) return null;

  const isArabic = i18n.language === "ar" || i18n.dir() === "rtl";

  const confirmButton = (
    <Button
      type="button"
      color="danger"
      onClick={confirmDelete}
      disabled={isLoading}
      className={isArabic ? "me-2" : ""}
    >
      {isLoading ? (
        <>
          <span
            className="spinner-border spinner-border-sm me-1"
            role="status"
            aria-hidden="true"
          />
          {t("Processing...")}
        </>
      ) : (
        <>
          {confirmIcon && <i className={`${confirmIcon} me-1`} />}
          {t(confirmText)}
        </>
      )}
    </Button>
  );

  const cancelButton = (
    <Button
      ref={cancelButtonRef}
      type="button"
      color="secondary"
      onClick={onClose}
      disabled={isLoading}
      className={isArabic ? "" : "me-2"}
    >
      {t("cancel")}
    </Button>
  );

  return (
    <div
      className="modal fade show d-block oh-modal"
      dir={isArabic ? "rtl" : "ltr"}
      lang={i18n.language}
      style={{
        backgroundColor: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
      }}
      onClick={handleBackdropClick}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{t("confirmaction")}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={isLoading}
              aria-label={t("close")}
            />
          </div>

          <div className="modal-body">
            <div className="d-flex align-items-start gap-3">
              <div>
                <i
                  className="fas fa-exclamation-triangle text-warning"
                  style={{ fontSize: "24px" }}
                />
              </div>
              <div>
                <p className="mb-0 fs-6">{text}</p>
              </div>
            </div>
          </div>

          <div className="modal-footer justify-content-end">
            {isArabic ? (
              <>
                {confirmButton}
                {cancelButton}
              </>
            ) : (
              <>
                {cancelButton}
                {confirmButton}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteModal;
